import React, { useEffect, useState } from 'react';
import { Button, Card, Form, Input, message, Typography, Alert } from 'antd';
import { KeyOutlined } from '@ant-design/icons';
import { useNavigate, useLocation } from 'react-router-dom';
import { api } from '../../http/API';

const { Title } = Typography;

const RESEND_COOLDOWN = 60;

const RegisterVerifyPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [loading, setLoading] = useState(false);
    const [resending, setResending] = useState(false);
    const [cooldown, setCooldown] = useState(0);

    const params = new URLSearchParams(location.search);
    const emailFromState = (location.state as { email?: string } | null)?.email;
    const emailFromQuery = params.get('email') || '';
    const email = emailFromState || emailFromQuery;
    const codeFromQuery = params.get('code') || '';

    useEffect(() => {
        if (!email) {
            navigate('/register', { replace: true });
        }
    }, [email, navigate]);

    useEffect(() => {
        if (cooldown <= 0) return undefined;
        const t = setInterval(() => setCooldown((c) => (c > 0 ? c - 1 : 0)), 1000);
        return () => clearInterval(t);
    }, [cooldown]);

    const onFinish = async (values: { code: string }) => {
        setLoading(true);
        try {
            await api.RegisterVerify(email, values.code.trim());
            message.success('Email подтверждён. Войдите в аккаунт.');
            navigate('/login');
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Неверный или просроченный код');
        } finally {
            setLoading(false);
        }
    };

    const resend = async () => {
        if (cooldown > 0) return;
        setResending(true);
        try {
            await api.RegisterResend(email);
            message.success('Код отправлен повторно');
            setCooldown(RESEND_COOLDOWN);
        } catch (error: any) {
            if (error?.response?.status === 429) {
                message.warning('Слишком часто. Подождите минуту.');
                setCooldown(RESEND_COOLDOWN);
            } else {
                message.error('Не удалось отправить код');
            }
        } finally {
            setResending(false);
        }
    };

    if (!email) return null;

    return (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh', background: '#f0f2f5', padding: 24 }}>
            <Card style={{ width: 420, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                <Title level={3} style={{ textAlign: 'center' }}>Подтверждение email</Title>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message={<>Код отправлен на <strong>{email}</strong></>}
                    action={
                        <Button size="small" type="link" onClick={() => navigate('/register')}>
                            Изменить
                        </Button>
                    }
                />
                <Form
                    layout="vertical"
                    onFinish={onFinish}
                    initialValues={codeFromQuery ? { code: codeFromQuery } : undefined}
                >
                    <Form.Item
                        label="Код из письма"
                        name="code"
                        rules={[
                            { required: true, message: 'Введите код' },
                            { len: 6, message: '6 цифр' },
                        ]}
                    >
                        <Input prefix={<KeyOutlined />} placeholder="6-значный код" maxLength={6} />
                    </Form.Item>
                    <Form.Item>
                        <Button type="primary" htmlType="submit" block loading={loading}>
                            Подтвердить
                        </Button>
                    </Form.Item>
                    <div style={{ textAlign: 'center' }}>
                        <Button type="link" onClick={resend} disabled={cooldown > 0} loading={resending}>
                            {cooldown > 0 ? `Отправить повторно через ${cooldown} с` : 'Отправить код повторно'}
                        </Button>
                    </div>
                </Form>
            </Card>
        </div>
    );
};

export default RegisterVerifyPage;
